import { formatDate } from "common/service/CommonService";
import { GetUserData } from "common/service/CommonService";
import {
  ContainerDeductionState,
  ContainerItem,
  ContainerReconciliation,
  DispensingRecord,
  DispensingSavePayload,
  DispensingUpdatePayload,
  SubjectAllocation,
} from "./dispensing.types";

/**
 * Calculates total quantity needed based on subject count, per subject quantity, and extra quantity.
 */
export const calculateTotalQuantityNeeded = (
  subjectCount: number,
  perSubjectQty: number | null,
  extraQuantity: number | null
): number | null => {
  const perSub = Number(perSubjectQty) || 0;
  const extra = Number(extraQuantity) || 0;
  const total = subjectCount * perSub + extra;
  return total > 0 ? total : null;
};

/**
 * Calculates net usable quantity across all selected containers after deductions.
 */
export const calculateNetUsableContainerQuantity = (
  selectedContainerDetails: ContainerItem[],
  deductions: ContainerDeductionState
): number => {
  return selectedContainerDetails.reduce((acc: number, c) => {
    const initial = Number(c.initialquantity || 0);
    const ded = Number(deductions[c.containernumber]?.deductedqty || 0);
    const finalQty = Math.max(0, initial - ded);
    return acc + finalQty;
  }, 0);
};

/**
 * Calculates total deducted quantity across all selected containers.
 */
export const calculateTotalDeductedQuantity = (
  selectedContainerDetails: ContainerItem[],
  deductions: ContainerDeductionState
): number => {
  return selectedContainerDetails.reduce((acc: number, c) => {
    const ded = Number(deductions[c.containernumber]?.deductedqty || 0);
    return acc + ded;
  }, 0);
};

/**
 * Calculates total initial available quantity across selected containers.
 */
export const calculateTotalInitialQuantity = (
  selectedContainerDetails: ContainerItem[]
): number => {
  return selectedContainerDetails.reduce(
    (acc: number, c) => acc + Number(c.initialquantity || 0),
    0
  );
};

/**
 * Maps raw API table rows into strongly-typed DispensingRecord objects.
 */
export const mapDispensingRecord = (row: any): DispensingRecord => {
  const dispensedByStr = row.dispensedby ?? "";
  const initials =
    row.dispensedbyinitials ??
    (dispensedByStr
      ? dispensedByStr
          .split(" ")
          .map((n: string) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2)
      : "?");

  const subjectRangeStr =
    row.subjectrange ??
    `${String(row.startsubjectno ?? "").padStart(2, "0")} - ${String(row.endsubjectno ?? "").padStart(2, "0")}`;

  return {
    dispensingid: row.dispensingid,
    logid: row.logid,
    studyprotocol: row.studyprotocol ?? row.studycode ?? "",
    studyname: row.studyname ?? row.studytypetext ?? "",
    subjectrange: subjectRangeStr,
    subjectcount: row.subjectcount ?? row.totalquantityneeded ?? 0,
    dispenseddate: row.dispenseddate ?? formatDate(row.dispensingdate),
    dispensedtime: row.dispensedtime ?? row.dispensingtime ?? "",
    dispensedby: dispensedByStr,
    dispensedbyinitials: initials,
    isunassigned: row.isunassigned ?? !row.dispensedby,
    total_count: row.total_count ?? 0,
    isverified: row.isverified ?? false,
    studyid: row.studyid ?? 0,
    productname: row.productname || "",
    ipcategory: row.ipcategory || "",
    batchlotno: row.batchlotno || "",
    totalquantity: row.totalquantityneeded ?? 0,
    qtypersubject: row.quantitypersubject ?? 0,
    verifiedbyname: row.verifiedbyname || "",
    verifieddate: row.verifieddate || "",
    expirydate: row.expirydate || "",
    availablequantity: row.availablequantity || 0,
    sponsorname: row.sponsorname || "",
  };
};

/**
 * Builds the payload for saving a new Dispensing Log record (Tab 1).
 */
export const buildSaveDispensingPayload = ({
  ipData,
  selectedStudyId,
  perSubjectQtyVal,
  totalQtyNeededVal,
  selectedPeriodNo,
  selectedContainerDetails,
  deductions,
}: {
  ipData: any;
  selectedStudyId: string | number;
  perSubjectQtyVal: any;
  totalQtyNeededVal: any;
  selectedPeriodNo: number | null;
  selectedContainerDetails: ContainerItem[];
  deductions: ContainerDeductionState;
}): DispensingSavePayload => {
  const user = GetUserData();
  const containerdetailsPayload = selectedContainerDetails.map((c) => {
    const ded = deductions[c.containernumber];
    const initial = Number(c.initialquantity || 0);
    const deductedqty = Number(ded?.deductedqty || 0);
    const reasonStr = ded?.reason ? String(ded.reason).trim() : "";
    const finalqty = Math.max(0, initial - deductedqty);

    return {
      ipdetailid: Number(c.ipdetailid),
      availableqty: initial,
      deductionreason: reasonStr.length > 0 ? reasonStr : null,
      deductedqty: deductedqty,
      finalqty: finalqty,
    };
  });

  const calculatedTotalQty = containerdetailsPayload.reduce(
    (acc, c) => acc + c.finalqty,
    0
  );

  return {
    ipid: Number(ipData?.ipid),
    studyid: Number(selectedStudyId),
    quantitypersubject: Number(perSubjectQtyVal),
    totalquantityneeded: Number(totalQtyNeededVal || calculatedTotalQty),
    periodid: Number(selectedPeriodNo),
    containerdetails: containerdetailsPayload,
    createdby: Number(user?.userid),
  };
};

/**
 * Builds the payload for updating an existing Dispensing Log record (Tab 1).
 */
export const buildUpdateDispensingPayload = ({
  editId,
  updateReason,
  ipData,
  selectedStudyId,
  perSubjectQtyVal,
  totalQtyNeededVal,
  selectedPeriodNo,
  selectedContainerDetails,
  originalContainerDetails,
  deductions,
}: {
  editId: string | number;
  updateReason: string;
  ipData: any;
  selectedStudyId: string | number;
  perSubjectQtyVal: any;
  totalQtyNeededVal: any;
  selectedPeriodNo: number | null;
  selectedContainerDetails: ContainerItem[];
  originalContainerDetails: any[];
  deductions: ContainerDeductionState;
}): DispensingUpdatePayload => {
  const user = GetUserData();
  const containerdetailsPayload: any[] = [];
  const origList = originalContainerDetails || [];

  // 1. Process items in originalContainerDetails (from DB)
  origList.forEach((orig: any) => {
    const currentlySelectedMatch = selectedContainerDetails.find(
      (c) => String(c.ipdetailid) === String(orig.ipdetailid)
    );

    if (currentlySelectedMatch) {
      // Modify / Keep existing container -> Action "U"
      const ded = deductions[currentlySelectedMatch.containernumber];
      const initial = Number(currentlySelectedMatch.initialquantity || orig.availableqty || 0);
      const deductedqty = Number(ded?.deductedqty || 0);
      const reasonStr = ded?.reason ? String(ded.reason).trim() : "";
      const finalqty = Math.max(0, initial - deductedqty);

      containerdetailsPayload.push({
        containerdetailid: orig.containerdetailid,
        action: "U",
        dispensingid: Number(editId),
        ipdetailid: Number(orig.ipdetailid),
        availableqty: initial,
        deductionreason: reasonStr.length > 0 ? reasonStr : null,
        deductedqty: deductedqty,
        finalqty: finalqty,
      });
    } else {
      // Deselected existing container -> Action "D"
      containerdetailsPayload.push({
        containerdetailid: orig.containerdetailid,
        action: "D",
        dispensingid: Number(editId),
        ipdetailid: Number(orig.ipdetailid),
        availableqty: Number(orig.availableqty || 0),
        deductionreason: orig.deductionreason || null,
        deductedqty: Number(orig.deductedqty || 0),
        finalqty: Number(orig.finalqty || 0),
      });
    }
  });

  // 2. Process newly selected containers not in originalContainerDetails -> Action "I"
  selectedContainerDetails.forEach((c) => {
    const isOriginal = origList.some(
      (orig: any) => String(orig.ipdetailid) === String(c.ipdetailid)
    );

    if (!isOriginal) {
      const ded = deductions[c.containernumber];
      const initial = Number(c.initialquantity || 0);
      const deductedqty = Number(ded?.deductedqty || 0);
      const reasonStr = ded?.reason ? String(ded.reason).trim() : "";
      const finalqty = Math.max(0, initial - deductedqty);

      containerdetailsPayload.push({
        action: "I",
        dispensingid: Number(editId),
        ipdetailid: Number(c.ipdetailid),
        availableqty: initial,
        deductionreason: reasonStr.length > 0 ? reasonStr : null,
        deductedqty: deductedqty,
        finalqty: finalqty,
      });
    }
  });

  const calculatedTotalQty = containerdetailsPayload
    .filter((c) => c.action !== "D")
    .reduce((acc, c) => acc + c.finalqty, 0);

  return {
    dispensingid: Number(editId),
    updatereason: updateReason,
    ipid: Number(ipData?.ipid),
    studyid: Number(selectedStudyId),
    quantitypersubject: Number(perSubjectQtyVal),
    totalquantityneeded: Number(totalQtyNeededVal || calculatedTotalQty),
    periodid: Number(selectedPeriodNo),
    containerdetails: containerdetailsPayload,
    updatedby: Number(user?.userid),
  };
};

/**
 * Recalculates dispensedToSubjects and remainingQty for each container.

/**
 * Recalculates dispensedToSubjects and remainingQty for each container.
 * remainingQty is ALWAYS derived — never set directly — so it can never drift
 * out of sync with the actual subject allocations + extra dispensing.
 */
export const recalculateContainers = (
  containers: ContainerReconciliation[]
): ContainerReconciliation[] => {
  return containers.map((c) => {
    const dispensed = (c.subjectAllocations || []).reduce(
      (sum, sa) => sum + Number(sa.qty || 0),
      0
    );
    const extra = Number(c.extraQty || 0);
    const net = Number(c.netUsableQty || 0);
    const remaining = Math.max(0, net - dispensed - extra);

    return {
      ...c,
      dispensedToSubjects: dispensed,
      remainingQty: remaining,
    };
  });
};

/**
 * Auto-assigns unallocated subjects to containers based on remaining capacity.
 *
 * IMPORTANT FIX: the previous version would partially allocate a subject
 * across containers and, if it still couldn't reach the full per-subject
 * quantity, leave that partial allocation sitting in the containers while
 * ALSO reporting the subject as "unallocated". That meant units could look
 * dispensed on a container's ledger for a subject who, per the UI, hadn't
 * received their dose yet — a real risk for a use case this quantity-critical.
 *
 * This version simulates each subject's assignment on a scratch copy first.
 * It only commits the allocation if the subject's full required quantity can
 * be satisfied (splitting across containers is still fully supported). If it
 * can't be fully satisfied, nothing is committed for that subject and it
 * stays in unallocatedSubjects untouched, so a subject is always either
 * fully allocated or not allocated at all — never silently partial.
 */
export const autoAssignSubjects = ({
  containers,
  unallocatedSubjects,
  perSubjectQty,
}: {
  containers: ContainerReconciliation[];
  unallocatedSubjects: string[];
  perSubjectQty: number;
}): {
  containers: ContainerReconciliation[];
  unallocatedSubjects: string[];
} => {
  let workingContainers = JSON.parse(
    JSON.stringify(containers)
  ) as ContainerReconciliation[];
  const remainingUnallocated: string[] = [];

  for (const subjectId of unallocatedSubjects) {
    const target = Number(perSubjectQty) || 0;
    if (target <= 0) {
      remainingUnallocated.push(subjectId);
      continue;
    }

    // Simulate the assignment on a scratch copy before touching real state.
    const attempt = JSON.parse(
      JSON.stringify(workingContainers)
    ) as ContainerReconciliation[];
    let needed = target;
    const touched: { containerId: string; qty: number }[] = [];

    for (const c of attempt) {
      if (needed <= 0) break;
      if (c.remainingQty > 0) {
        const allocQty = Math.min(c.remainingQty, needed);
        touched.push({ containerId: c.containerId, qty: allocQty });
        c.remainingQty -= allocQty;
        needed -= allocQty;
      }
    }

    if (needed === 0) {
      // Fully satisfiable — commit the touched allocations for real.
      touched.forEach(({ containerId, qty }) => {
        const target = workingContainers.find((c) => c.containerId === containerId);
        if (!target) return;
        const existing = target.subjectAllocations.find(
          (s: SubjectAllocation) => s.subjectId === subjectId
        );
        if (existing) {
          existing.qty += qty;
        } else {
          target.subjectAllocations.push({ subjectId, qty });
        }
      });
      workingContainers = recalculateContainers(workingContainers);
    } else {
      // Could not fully satisfy this subject from any combination of
      // remaining capacity — leave it untouched and unallocated.
      remainingUnallocated.push(subjectId);
    }
  }

  return {
    containers: workingContainers,
    unallocatedSubjects: remainingUnallocated,
  };
};

/**
 * Gets total quantity allocated for a subject across all containers.
 */
export const getSubjectTotalAllocated = (
  containers: ContainerReconciliation[],
  subjectId: string
): number => {
  let total = 0;
  containers.forEach((c) => {
    const alloc = c.subjectAllocations.find(
      (s: SubjectAllocation) => s.subjectId === subjectId
    );
    if (alloc) {
      total += Number(alloc.qty || 0);
    }
  });
  return total;
};

/**
 * Returns breakdown of containers and quantities for a subject.
 */
export const getSubjectContainerSplits = (
  containers: ContainerReconciliation[],
  subjectId: string
): { containerId: string; qty: number }[] => {
  const splits: { containerId: string; qty: number }[] = [];
  containers.forEach((c) => {
    const alloc = c.subjectAllocations.find(
      (s: SubjectAllocation) => s.subjectId === subjectId
    );
    if (alloc && alloc.qty > 0) {
      splits.push({ containerId: c.containerId, qty: alloc.qty });
    }
  });
  return splits;
};

/**
 * How much of a container a given subject could validly claim right now —
 * i.e. its current remaining capacity PLUS whatever that subject already
 * holds in this container (since that qty is about to be replaced, not
 * added on top of, when the split editor saves). Use this instead of raw
 * remainingQty anywhere a subject's own split is being edited, otherwise
 * the UI understates how much room they actually have to work with.
 */
export const getAvailableForSplit = (
  containers: ContainerReconciliation[],
  subjectId: string,
  containerId: string
): number => {
  const c = containers.find((c) => c.containerId === containerId);
  if (!c) return 0;
  const existing = c.subjectAllocations.find(
    (s: SubjectAllocation) => s.subjectId === subjectId
  );
  return c.remainingQty + (existing ? Number(existing.qty || 0) : 0);
};

/**
 * Updates container allocations for a specific subject based on custom split
 * configuration. Validation of totals/capacity is expected to happen in the
 * caller (component) BEFORE calling this, using getAvailableForSplit.
 */

export const updateSubjectSplit = ({
  containers,
  subjectId,
  newSplits,
}: {
  containers: ContainerReconciliation[];
  subjectId: string;
  newSplits: { containerId: string; qty: number }[];
}): ContainerReconciliation[] => {
  let updatedContainers = JSON.parse(
    JSON.stringify(containers)
  ) as ContainerReconciliation[];

  // First remove all existing allocations for subjectId
  updatedContainers.forEach((c) => {
    c.subjectAllocations = c.subjectAllocations.filter(
      (s: SubjectAllocation) => s.subjectId !== subjectId
    );
  });

  // Apply new splits
  newSplits.forEach((split) => {
    if (split.qty > 0 && split.containerId) {
      const targetC = updatedContainers.find((c) => c.containerId === split.containerId);
      if (targetC) {
        targetC.subjectAllocations.push({ subjectId, qty: Number(split.qty) });
      }
    }
  });

  return recalculateContainers(updatedContainers);
};

/**
 * Clears every allocation a subject has, across all containers, returning
 * them to the unallocated pool. Used by the "remove" action on a subject row.
 */
export const clearSubjectAllocation = (
  containers: ContainerReconciliation[],
  subjectId: string
): ContainerReconciliation[] => {
  const updated = containers.map((c) => ({
    ...c,
    subjectAllocations: c.subjectAllocations.filter(
      (s: SubjectAllocation) => s.subjectId !== subjectId
    ),
  }));
  return recalculateContainers(updated);
};

/**
 * Clears ALL allocations for every subject in every container — a full reset.
 */
export const clearAllAllocations = (
  containers: ContainerReconciliation[]
): ContainerReconciliation[] => {
  const updated = containers.map((c) => ({
    ...c,
    subjectAllocations: [],
  }));
  return recalculateContainers(updated);
};

export type SubjectStatus = "unallocated" | "partial" | "over" | "split" | "allocated";

export interface SubjectRowData {
  subjectId: string;
  total: number;
  splits: { containerId: string; qty: number }[];
  status: SubjectStatus;
}

/** 
 * Builds the per-subject status list the subject-wise view renders from.
 * A subject is only ever "allocated" or "split" when its total exactly
 * equals the required per-subject quantity — a partial or over-allocated
 * subject is always flagged, never silently treated as done.
 */
export const buildSubjectRows = (
  subjectIds: string[],
  containers: ContainerReconciliation[],
  perSubjectQty: number
): SubjectRowData[] => {
  const target = Number(perSubjectQty) || 0;

  return subjectIds.map((subjectId) => {
    const splits = getSubjectContainerSplits(containers, subjectId);
    const total = splits.reduce((sum, s) => sum + Number(s.qty || 0), 0);

    let status: SubjectStatus;
    if (total === 0) {
      status = "unallocated";
    } else if (total > target) {
      status = "over";
    } else if (total < target) {
      status = "partial";
    } else {
      status = splits.length > 1 ? "split" : "allocated";
    }

    return { subjectId, total, splits, status };
  });
};